import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import ACTION from "../../store/action";
import {
  selectBulkSelectedRecordIds,
  selectBulkSelectionMode,
  selectBulkSelectionCount,
  selectBulkActionLoading,
  selectBulkActionResult,
  selectBulkActionNavigatedAway
} from "../../store/selector";
import { BULK_SELECTION_LIMIT, isSelectionLimitReached } from "./bulkActionUtils";

/**
 * Hook for managing bulk selection state
 * @param {string} viewKey - Unique key for the view (e.g., 'all-patients')
 * @param {number[]} visibleRecordIds - IDs of records currently visible on the page
 * @param {number} totalRecords - Total number of records in the current view
 * @returns {Object} Bulk selection state and handlers
 */
const useBulkSelection = (viewKey, visibleRecordIds = [], totalRecords = 0) => {
  const dispatch = useDispatch();

  // Selectors
  const selectedRecordIds = useSelector(selectBulkSelectedRecordIds(viewKey));
  const selectionMode = useSelector(selectBulkSelectionMode(viewKey));
  const selectionCount = useSelector(selectBulkSelectionCount(viewKey));
  const isLoading = useSelector(selectBulkActionLoading);
  const actionResult = useSelector(selectBulkActionResult);
  const hasNavigatedAway = useSelector(selectBulkActionNavigatedAway);

  // Computed values
  const isLimitReached = useMemo(() => isSelectionLimitReached(selectionCount), [selectionCount]);

  const isAllPageSelected = useMemo(() => {
    if (visibleRecordIds.length === 0) return false;
    return visibleRecordIds.every((id) => selectedRecordIds.includes(id));
  }, [visibleRecordIds, selectedRecordIds]);

  const isSomePageSelected = useMemo(() => {
    if (visibleRecordIds.length === 0) return false;
    return visibleRecordIds.some((id) => selectedRecordIds.includes(id));
  }, [visibleRecordIds, selectedRecordIds]);

  const isIndeterminate = useMemo(() => {
    return isSomePageSelected && !isAllPageSelected;
  }, [isSomePageSelected, isAllPageSelected]);

  const hasSelection = selectionCount > 0;

  // Calculate how many unselected records are on the current page
  const unselectedOnPageCount = useMemo(() => {
    return visibleRecordIds.filter((id) => !selectedRecordIds.includes(id)).length;
  }, [visibleRecordIds, selectedRecordIds]);

  // Check if selecting all unselected records on page would exceed the limit
  const wouldExceedLimitOnPageSelect = useMemo(() => {
    // If all records on page are already selected, no need to disable
    if (isAllPageSelected) return false;
    // Check if adding all unselected page records would exceed limit
    return selectionCount + unselectedOnPageCount > BULK_SELECTION_LIMIT;
  }, [selectionCount, unselectedOnPageCount, isAllPageSelected]);

  // Handlers
  const selectRecord = useCallback(
    (recordId) => {
      if (!isLimitReached) {
        dispatch(ACTION.addToBulkSelection(viewKey, recordId));
      }
    },
    [dispatch, viewKey, isLimitReached]
  );

  const deselectRecord = useCallback(
    (recordId) => {
      dispatch(ACTION.removeFromBulkSelection(viewKey, recordId));
    },
    [dispatch, viewKey]
  );

  const toggleRecord = useCallback(
    (recordId) => {
      if (selectedRecordIds.includes(recordId)) {
        deselectRecord(recordId);
      } else {
        selectRecord(recordId);
      }
    },
    [selectedRecordIds, selectRecord, deselectRecord]
  );

  const selectPage = useCallback(() => {
    const currentCount = selectionCount;
    const newIds = visibleRecordIds.filter((id) => !selectedRecordIds.includes(id));
    const idsToAdd = newIds.slice(0, BULK_SELECTION_LIMIT - currentCount);

    if (idsToAdd.length > 0) {
      const newSelection = [...selectedRecordIds, ...idsToAdd];
      dispatch(ACTION.setBulkSelection(viewKey, newSelection));
    }
  }, [dispatch, viewKey, visibleRecordIds, selectedRecordIds, selectionCount]);

  const deselectPage = useCallback(() => {
    const newSelection = selectedRecordIds.filter((id) => !visibleRecordIds.includes(id));
    dispatch(ACTION.setBulkSelection(viewKey, newSelection));
  }, [dispatch, viewKey, visibleRecordIds, selectedRecordIds]);

  const togglePageSelection = useCallback(() => {
    if (isAllPageSelected) {
      deselectPage();
    } else {
      selectPage();
    }
  }, [isAllPageSelected, selectPage, deselectPage]);

  const selectAll = useCallback(
    (allIds) => {
      const idsToSelect = allIds.slice(0, BULK_SELECTION_LIMIT);
      dispatch(ACTION.setBulkSelection(viewKey, idsToSelect));
      dispatch(ACTION.setBulkSelectionMode(viewKey, "all"));
    },
    [dispatch, viewKey]
  );

  const clearSelection = useCallback(() => {
    dispatch(ACTION.clearBulkSelection(viewKey));
  }, [dispatch, viewKey]);

  const setLoading = useCallback(
    (loading) => {
      dispatch(ACTION.setBulkActionLoading(loading));
    },
    [dispatch]
  );

  const setResult = useCallback(
    (result) => {
      dispatch(ACTION.setBulkActionResult(result));
    },
    [dispatch]
  );

  const clearResult = useCallback(() => {
    dispatch(ACTION.clearBulkActionResult());
  }, [dispatch]);

  const setNavigatedAway = useCallback(
    (navigatedAway) => {
      dispatch(ACTION.setBulkActionNavigatedAway(navigatedAway));
    },
    [dispatch]
  );

  const isRecordSelected = useCallback(
    (recordId) => {
      return selectedRecordIds.includes(recordId);
    },
    [selectedRecordIds]
  );

  return {
    // State
    selectedRecordIds,
    selectionMode,
    selectionCount,
    isLoading,
    actionResult,
    hasNavigatedAway,

    // Computed
    isLimitReached,
    isAllPageSelected,
    isSomePageSelected,
    isIndeterminate,
    hasSelection,
    totalRecords,
    maxSelectable: Math.min(totalRecords, BULK_SELECTION_LIMIT),
    wouldExceedLimitOnPageSelect,

    // Handlers
    selectRecord,
    deselectRecord,
    toggleRecord,
    selectPage,
    deselectPage,
    togglePageSelection,
    selectAll,
    clearSelection,
    isRecordSelected,
    setLoading,
    setResult,
    clearResult,
    setNavigatedAway
  };
};

export default useBulkSelection;
